import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { displayKeys } from '@/features/displays/api'
import { api, unwrap } from '@/lib/api'
import type { DataEnvelope, DisplayGroup, Tag } from '@/types/api'

export interface GroupInput {
  name: string
  description?: string | null
  color?: string | null
  /** Phase B: tie-breaker between groups that set the same variable (−1000..1000). */
  variable_priority?: number
}

export interface TagInput {
  name: string
  color?: string | null
}

export const groupKeys = {
  all: ['groups'] as const,
  list: () => [...groupKeys.all, 'list'] as const,
}

export const tagKeys = {
  all: ['tags'] as const,
  list: () => [...tagKeys.all, 'list'] as const,
}

export const groupsQueryOptions = queryOptions({
  queryKey: groupKeys.list(),
  queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<DisplayGroup[]>>('/groups', { signal })),
})

export const tagsQueryOptions = queryOptions({
  queryKey: tagKeys.list(),
  queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<Tag[]>>('/tags', { signal })),
})

export function useGroups(enabled = true) {
  return useQuery({ ...groupsQueryOptions, enabled })
}

export function useTags(enabled = true) {
  return useQuery({ ...tagsQueryOptions, enabled })
}

function invalidateAll(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: groupKeys.all })
  void qc.invalidateQueries({ queryKey: tagKeys.all })
  // Displays embed group/tag chips.
  void qc.invalidateQueries({ queryKey: displayKeys.all })
}

export function useSaveGroup(id?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: GroupInput) =>
      unwrap(id ? api.patch<DataEnvelope<DisplayGroup>>(`/groups/${id}`, input) : api.post<DataEnvelope<DisplayGroup>>('/groups', input)),
    onSuccess: () => invalidateAll(qc),
  })
}

export function useDeleteGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/groups/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: groupKeys.list() })
      const prev = qc.getQueryData<DisplayGroup[]>(groupKeys.list())
      if (prev) qc.setQueryData(groupKeys.list(), prev.filter((g) => g.id !== id))
      return { prev }
    },
    onError: (_e, _id, ctx) => ctx?.prev && qc.setQueryData(groupKeys.list(), ctx.prev),
    onSettled: () => invalidateAll(qc),
  })
}

/** Applies a membership diff: POST adds, DELETE removes (both take `{display_ids}`). */
export function useUpdateGroupMembers(groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ add, remove }: { add: string[]; remove: string[] }) => {
      if (add.length) await api.post(`/groups/${groupId}/displays`, { display_ids: add })
      if (remove.length) await api.delete(`/groups/${groupId}/displays`, { display_ids: remove })
    },
    onSettled: () => invalidateAll(qc),
  })
}

export function useSaveTag(id?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: TagInput) => unwrap(id ? api.patch<DataEnvelope<Tag>>(`/tags/${id}`, input) : api.post<DataEnvelope<Tag>>('/tags', input)),
    onSuccess: () => invalidateAll(qc),
  })
}

export function useDeleteTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/tags/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: tagKeys.list() })
      const prev = qc.getQueryData<Tag[]>(tagKeys.list())
      if (prev) qc.setQueryData(tagKeys.list(), prev.filter((t) => t.id !== id))
      return { prev }
    },
    onError: (_e, _id, ctx) => ctx?.prev && qc.setQueryData(tagKeys.list(), ctx.prev),
    onSettled: () => invalidateAll(qc),
  })
}
